import { useAtom } from "jotai";
import isEqual from "lodash-es/isEqual";
import { useTranslation } from "react-i18next";

import { cn } from "~/components/ui/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ArrowDownIcon } from "~/components/ui/icons/arrow-down";
import { stockSortState } from "~/views/stock/stock-state";

import {
  ACTIVE_ALERTS_COUNT_ACCESSOR_KEY,
  GTIN_ACCESSOR_KEY,
  POTENTIAL_REVENUE_ACCESSOR_KEY,
  PRODUCT_NAME_ACCESSOR_KEY,
  SKU_ACCESSOR_KEY,
  STOCK_ACCESSOR_KEY,
  TOTAL_ALERTS_COUNT_ACCESSOR_KEY,
} from "./stock-product-list";

export function ProductSortDropdownMenu({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [stockSort, setStockSort] = useAtom(stockSortState);

  const sortOptions = [
    {
      id: "name_asc",
      label: t("stock.sort.name_asc"),
      value: [
        {
          id: PRODUCT_NAME_ACCESSOR_KEY,
          desc: false,
        },
      ],
    },
    {
      id: "name_desc",
      label: t("stock.sort.name_desc"),
      value: [
        {
          id: PRODUCT_NAME_ACCESSOR_KEY,
          desc: true,
        },
      ],
    },
    {
      id: "sku_asc",
      label: t("stock.sort.sku_asc"),
      value: [
        {
          id: SKU_ACCESSOR_KEY,
          desc: false,
        },
      ],
    },
    {
      id: "sku_desc",
      label: t("stock.sort.sku_desc"),
      value: [
        {
          id: SKU_ACCESSOR_KEY,
          desc: true,
        },
      ],
    },
    {
      id: "gtin_asc",
      label: t("stock.sort.gtin_asc"),
      value: [
        {
          id: GTIN_ACCESSOR_KEY,
          desc: false,
        },
      ],
    },
    {
      id: "gtin_desc",
      label: t("stock.sort.gtin_desc"),
      value: [
        {
          id: GTIN_ACCESSOR_KEY,
          desc: true,
        },
      ],
    },
    {
      id: "total_alerts_count_asc",
      label: t("stock.sort.total_alerts_count_asc"),
      value: [
        {
          id: TOTAL_ALERTS_COUNT_ACCESSOR_KEY,
          desc: false,
        },
      ],
    },
    {
      id: "total_alerts_count_desc",
      label: t("stock.sort.total_alerts_count_desc"),
      value: [
        {
          id: TOTAL_ALERTS_COUNT_ACCESSOR_KEY,
          desc: true,
        },
      ],
    },
    {
      id: "potential_revenue_asc",
      label: t("stock.sort.potential_revenue_asc"),
      value: [
        {
          id: POTENTIAL_REVENUE_ACCESSOR_KEY,
          desc: false,
        },
      ],
    },
    {
      id: "potential_revenue_desc",
      label: t("stock.sort.potential_revenue_desc"),
      value: [
        {
          id: POTENTIAL_REVENUE_ACCESSOR_KEY,
          desc: true,
        },
      ],
    },
    {
      id: "active_alerts_count_asc",
      label: t("stock.sort.active_alerts_count_asc"),
      value: [
        {
          id: ACTIVE_ALERTS_COUNT_ACCESSOR_KEY,
          desc: false,
        },
      ],
    },
    {
      id: "active_alerts_count_desc",
      label: t("stock.sort.active_alerts_count_desc"),
      value: [
        {
          id: ACTIVE_ALERTS_COUNT_ACCESSOR_KEY,
          desc: true,
        },
      ],
    },
    {
      id: "stock_asc",
      label: t("stock.sort.stock_asc"),
      value: [
        {
          id: STOCK_ACCESSOR_KEY,
          desc: false,
        },
      ],
    },
    {
      id: "stock_desc",
      label: t("stock.sort.stock_desc"),
      value: [
        {
          id: STOCK_ACCESSOR_KEY,
          desc: true,
        },
      ],
    },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn("group mb-2 flex shrink-0 items-center", className)}
      >
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-green-900">
            {t("stock.sort.label", {
              sort: sortOptions.find((c) => isEqual(c.value, stockSort))?.label,
            })}
          </span>
          <ArrowDownIcon className="size-4 shrink-0 transition-all group-data-[state=open]:rotate-180" />
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="min-w-40 overflow-y-auto"
        style={{
          maxHeight: "var(--radix-dropdown-menu-content-available-height)",
        }}
      >
        {sortOptions.map((sortOption) => (
          <DropdownMenuItem
            key={sortOption.id}
            onSelect={() => setStockSort(sortOption.value)}
          >
            {sortOption.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
